import React, { useState } from "react";
import { Modal } from "../core/Modal/Modal";
import { CANCEL_LABEL, CONFIRM_LABEL } from "../../constants/Dictionary";
import { Button, ButtonVariants } from "../core/Button/Button";
import { RadioButton } from "../core/RadioButton/RadioButton";
import { FontWeightEnum } from "../../types/AppTypes";
import { BodyTyp } from "../Typography/Typography";
import { ReactComponent as CloseIcon } from "../../icons/close.svg";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { StyledModalSelectRadio } from "./ModalSelectRadio.styled";
import { Loader } from "../core/Loader/Loader";

interface ModalSelectRadioProps<T> {
  label: string;
  isOpen: boolean;
  defaultSelected?: T;
  onConfirm: (item: T) => void;
  onCancel: () => void;
  radioSelectionItems: T[];
  isLoading?: boolean;
}

export const ModalSelectRadio = <T extends string | number>({
  label,
  isOpen,
  defaultSelected,
  onConfirm,
  onCancel,
  radioSelectionItems,
  isLoading,
}: ModalSelectRadioProps<T>) => {
  const [tmpValue, setTmpValue] = useState<T | null>(defaultSelected ?? null);

  const handleConfirm = () => {
    onConfirm(tmpValue as T);
    setTmpValue(null);
  };

  const handleCancel = () => {
    onCancel();
    setTmpValue(null);
  };

  return (
    <Modal
      label={label}
      isOpen={isOpen}
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          onClick={handleConfirm}
          icon={<CheckIcon />}
          disabled={tmpValue === null || isLoading}
        />
      }
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          onClick={handleCancel}
          icon={<CloseIcon />}
        />
      }
    >
      <Loader size={30} loading={isLoading ?? false}>
        <StyledModalSelectRadio>
          {radioSelectionItems.map((item, index) => (
            <div className="radio-item" key={`${item}-${index}`}>
              <RadioButton
                value={tmpValue === item}
                onChange={() => {
                  setTmpValue(item);
                }}
              />
              <BodyTyp fontWeight={FontWeightEnum.MEDIUM} variant="2">
                {typeof item === "string" ? item : `${item}%`}
              </BodyTyp>
            </div>
          ))}
        </StyledModalSelectRadio>
      </Loader>
    </Modal>
  );
};
